import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { RentDatePickerSection } from "@/components/rent/rent-date-picker-section"
import { RentPaymentPlanOptions } from "@/components/rent/rent-payment-plan-options"
import { RentPaymentSettingsRow } from "@/components/rent/rent-payment-settings-row"
import { RentPaymentSummary } from "@/components/rent/rent-payment-summary"
import type { RentPaymentPlanOption, RentPeriod } from "@/components/rent/rent-constants"
import type { PeriodPrice } from "@/types/price"
import type { FacilityWithUnitTypes, UnitType } from "@/types/warehouse"

type PaymentStepProps = {
  isActive: boolean
  isMobile: boolean
  startedAt: string
  onStartedAtChange: (value: string) => void
  period: RentPeriod | ""
  onPeriodChange: (value: RentPeriod) => void
  paymentPlanOptions: RentPaymentPlanOption[]
  isPricingLoading?: boolean
  planErrorMessage?: string | null
  promoDraft: string
  onPromoDraftChange: (value: string) => void
  onApplyPromo: () => void
  appliedPromo?: string
  promoErrorMessage?: string | null
  isRecurrent: boolean
  onRecurrentChange: (value: boolean) => void
  selectedFacility?: FacilityWithUnitTypes["facility"]
  selectedUnitType?: UnitType
  selectedPrice?: PeriodPrice | null
  paymentError?: string | null
  isSubmitting?: boolean
  isPromoApplying?: boolean
  footer?: React.ReactNode
}

export function PaymentStep({
  isActive,
  isMobile,
  startedAt,
  onStartedAtChange,
  period,
  onPeriodChange,
  paymentPlanOptions,
  isPricingLoading = false,
  planErrorMessage = null,
  promoDraft,
  onPromoDraftChange,
  onApplyPromo,
  appliedPromo = "",
  promoErrorMessage = null,
  isRecurrent,
  onRecurrentChange,
  selectedFacility,
  selectedUnitType,
  selectedPrice = null,
  paymentError,
  isSubmitting,
  isPromoApplying = false,
  footer,
}: PaymentStepProps) {
  const planOptionsRef = useRef<HTMLDivElement>(null)
  const settingsRef = useRef<HTMLDivElement>(null)
  const prevStartedAtRef = useRef(startedAt)
  const prevPeriodRef = useRef(period)
  const isDateSelected = startedAt.length > 0

  useEffect(() => {
    if (!isActive || !isMobile || !startedAt) {
      return
    }

    const previousStartedAt = prevStartedAtRef.current
    prevStartedAtRef.current = startedAt

    if (startedAt === previousStartedAt) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      planOptionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [isActive, isMobile, startedAt])

  useEffect(() => {
    if (!isActive || !isMobile || !startedAt || !period) {
      return
    }

    const previousPeriod = prevPeriodRef.current
    prevPeriodRef.current = period

    if (period === previousPeriod) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      settingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [isActive, isMobile, period, startedAt])

  const formContent = (
    <div className="flex min-w-0 flex-col gap-8">
      <RentDatePickerSection
        value={startedAt}
        onValueChange={onStartedAtChange}
        disabled={isSubmitting}
      />

      <div ref={planOptionsRef} className="scroll-mt-2">
        <RentPaymentPlanOptions
          value={period}
          onValueChange={onPeriodChange}
          options={paymentPlanOptions}
          disabled={!isDateSelected || isSubmitting}
          isLoading={isDateSelected && isPricingLoading}
          errorMessage={isDateSelected ? planErrorMessage : null}
        />
      </div>

      <div ref={settingsRef} className="scroll-mt-2">
        <RentPaymentSettingsRow
          isRecurrent={isRecurrent}
          onRecurrentChange={onRecurrentChange}
          promoDraft={promoDraft}
          onPromoDraftChange={onPromoDraftChange}
          onApplyPromo={onApplyPromo}
          appliedPromo={appliedPromo}
          promoError={promoErrorMessage}
          disabled={!isDateSelected}
          isSubmitting={isSubmitting}
          isApplying={isPromoApplying}
        />
      </div>

      {paymentError ? <p className="text-sm text-destructive">{paymentError}</p> : null}
      {isSubmitting ? (
        <p className="text-sm text-light-muted">Создаём договор и готовим оплату...</p>
      ) : null}
    </div>
  )

  return (
    <div className={cn("flex min-w-0 gap-2", isMobile ? "flex-col" : "h-full")}>
      <div
        className={cn(
          "relative min-w-0 grow overflow-x-hidden rounded-[8px] bg-card p-2 lg:p-8",
          !isMobile && "min-h-[480px] flex-[58%]",
        )}
      >
        {formContent}
      </div>

      <div className="relative h-full min-h-[480px] min-w-0 grow flex-[42%] rounded-[8px] bg-card lg:p-8 p-2">
        <RentPaymentSummary
          facility={selectedFacility}
          unitType={selectedUnitType}
          startedAt={startedAt}
          period={period}
          totalPrice={selectedPrice?.total ?? null}
          totalBefore={selectedPrice?.totalBefore ?? null}
          footer={footer}
        />
      </div>
    </div>
  )
}
